import React, {
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import TranscriptContext from "../context/TranscriptContext";
import { isMobileOnly } from "react-device-detect";
import moment from "moment";
import momentTz from "moment-timezone";
import ReactGA from "react-ga";
import io from "socket.io-client";

import "./index.css";

// Helpers
import { checkLayout, getBrandingBackgroundColor } from "../helpers";

// Context
import WebcastingContext from "../context/webcasting/webcastingContext";

// Layouts
import PlayerOnly from "./layouts/PlayerOnly";
import PlayerSlides from "./layouts/PlayerSlides";
import ReversedPlayerSlides from "./layouts/ReversedPlayerSlides";
import PlayerChapters from "./layouts/PlayerChapters";
import PlayerSlidesChapters from "./layouts/PlayerSlidesChapters";
import ReversedPlayerSlidesChapters from "./layouts/ReversedPlayerSlidesChapters";
import UpcomingEvent from "./UpcomingEvent";

// Components
import HeaderBar from "../components/header/HeaderBar";
import BackgroundImg from "../components/UI/BackgroundImg";
import Toolbar from "../components/toolbar";
import HeadStyle from "./HeadStyle";
import HeadStyleForNoReg from "./HeadStyleForNoReg";
import SocketIOHOC from "../components/websocket/SocketIOHOC";
import useConditionalTimeout from "../hooks/useConditionalTimeout";
import LeaveUserModal from "../components/UI/LeaveUserModal";
import { submitLogs } from "../config/AWSGlobalLog";
import TranscriptDrawer, { TRANSCRIPT_PANEL_W } from "../components/transcript";

const Thumbs = lazy(() => import("../components/carousel/Thumbs"));

function isLogoTypeWebsite(logo) {
  return logo?.type === "website";
}

function getWebsiteLogoPath(event) {
  console.log('eventFromgetWebsiteLogoPath', event?.webcast?.branding?.logoSelection?.path)
  // const logos = event?.companies?.[0]?.logos || [];
  // const websiteLogo = logos.find(isLogoTypeWebsite);
  // return websiteLogo?.upload?.path || null;
  if(event?.webcast?.branding?.logoSelection?.path) {
    console.log('logoPathFromgetWebsiteLogoPathSelection',event?.webcast?.branding?.logoSelection)
    return event?.webcast?.branding?.logoSelection?.path || null;
  } 
  if(event){
    let temppath = event?.companies?.[0]?.logos?.find(isLogoTypeWebsite)?.upload?.path || null
    console.log('logoPathFromgetWebsiteLogoPathSelectionvent', `${process.env.REACT_APP_ASSETS_BASE_URL}/${temppath}`)
    return `${process.env.REACT_APP_ASSETS_BASE_URL}/${temppath}`
  }

}

function getLogoPadding(event) {
  const padding = event?.webcast?.branding?.logo?.padding;
  return padding && padding !== "" ? padding : "10px";
}

function getHeaderTitle(event) {
  return event?.headerTitle || event?.title || "";
}

function getHeaderBackground(event) {
  const brandingColor = getBrandingBackgroundColor(
    event?.webcast?.branding?.background
  );
  if (brandingColor) return brandingColor;
  const legacy = event?.webcast?.colour?.background;
  return legacy && legacy !== "" ? legacy : "#ffffff";
}

function getUserId({ registrationRequired, authorization, noRegID }) {
  console.log('authorizationFromgetUserId',  registrationRequired, noRegID)
  if (!registrationRequired) return noRegID || null;
  return authorization?.findId || null;
}

// function timeToEventMs(event) {
//   const start = moment(event?.releaseTime, "YYYY-MM-DDTHH:mm:ss.SSSZ");
//   const now = moment();
//   return start.diff(now);
// }

function timeToEventMs(event) {
  const start = moment(event?.releaseTime, "YYYY-MM-DDTHH:mm:ss.SSSZ");
  const now = moment();
  return start.diff(now);
}

function msUntilStart(event) {
  const isBST = momentTz.tz(event?.releaseTime, "Europe/London").isDST();
  return timeToEventMs(event) - (isBST ? 3600000 : 0);
}

function renderBackground({ event, isTemplateClean }) {
  if (isTemplateClean) return null;

  const bg = event?.webcast?.branding?.background;
  const type = bg?.type;
  if (!type) return null;

  const opacity = bg.opacity != null ? bg.opacity : 1;

  // Respect type exclusively — do not fall back across image / color / video
  if (type === "image") {
    const imagePath = bg.image?.path;
    if (!imagePath) return null;
    return (
      <BackgroundImg
        path={imagePath}
        img={true}
        colour={null}
        opacity={opacity}
      />
    );
  }

  if (type === "video") {
    const videoPath = bg.video?.path;
    if (!videoPath) return null;
    return (
      <BackgroundImg
        path={videoPath}
        video={true}
        colour={null}
        opacity={opacity}
      />
    );
  }

  if (type === "color") {
    if (bg.color == null) return null;
    return (
      <BackgroundImg
        path={null}
        img={false}
        colour={bg.color}
        opacity={opacity}
      />
    );
  }

  return null;
}

/**
 * NEW: computeActiveRegions using the *object* layout shape
 *
 * We still return:
 * - activeRegionsApi: ["Video", "Slides"], ["Audio", "Slides", "Chapters"], etc
 * - withThumbs: whether to render the Thumbs carousel
 * - presenterMode: "on" | "off"
 */
function computeActiveRegions(event) {
  const layout = event?.webcast?.layout || {};
  console.log('layoutfromWebcastingPlatform', layout)
  const assetType = (event?.assetType || "").toLowerCase();

  // Base media region is derived from assetType, not layout booleans
  const baseRegion = assetType === "audio" ? "Audio" : "Video";

  const activeRegionsApi = [baseRegion];

  // Handle new structure: object with boolean flags
  // Helper to check truthy values (handles boolean true, string "true", etc.)
  const isTruthy = (val) => {
    if (val === true || val === "true" || val === 1 || val === "1") return true;
    return false;
  };

  // Check for slides (lowercase property name)
  if (isTruthy(layout.slides)) {
    activeRegionsApi.push("Slides");
  }

  // Check for chapters (lowercase property name)
  if (isTruthy(layout.chapters)) {
    activeRegionsApi.push("Chapters");
  }

  // Thumbnails are handled separately (Thumbs strip)
  const withThumbs = isTruthy(layout.thumbnails) || isTruthy(layout.thumbs);

  // Presenter shown or not
  const presenterMode = (isTruthy(layout.presenters) || isTruthy(layout.presenter)) ? "on" : "off";


  console.log('withThumbs', withThumbs)

  return { activeRegionsApi, withThumbs, presenterMode };
}

/**
 * Layout picker now takes activeRegionsApi from computeActiveRegions
 */
function pickLayoutComponent({
  event,
  socketWc,
  preview,
  withThumbs,
  presenterMode,
  activeRegionsApi,
}) {
  const assetType = (event?.assetType || "").toLowerCase();
  const layoutObj = event?.webcast?.layout || {};

  // reversePlatform (new API) falls back to old reverseLayout
  const reverseLayout =
    typeof layoutObj.reversePlatform === "boolean"
      ? layoutObj.reversePlatform
      : Boolean(event?.webcast?.reverseLayout);

  const audio = ["Audio"];
  const video = ["Video"];
  const audioSlidesLayout = ["Audio", "Slides"];
  const videoSlidesLayout = ["Slides", "Video"];
  const audioChaptersLayout = ["Chapters", "Audio"];
  const videoChaptersLayout = ["Chapters", "Video"];
  const audioSlidesChaptersLayout = ["Audio", "Slides", "Chapters"];
  const videoSlidesChaptersLayout = ["Video", "Slides", "Chapters"];

  const active = activeRegionsApi || [];
  const logPickedLayout = (layoutName) => {
    console.log("pickLayoutComponent rendering", {
      layoutName,
      activeRegionsApi: active,
      assetType,
      reverseLayout,
      withThumbs,
      presenterMode,
      socketReady: Boolean(socketWc),
    });
  };

  const isOnlyVideo =
    (active.length === 0 && assetType === "video") ||
    (active.length === 1 && active[0] === "Video");

  // If socket isn’t ready yet, keep behaviour of gating player layouts
  if (!socketWc) {
    logPickedLayout("None (socket not ready)");
    return null;
  }

  // Player only (Video or Audio only)
  if (
    active.length === 0 ||
    checkLayout(active, video) ||
    checkLayout(active, audio)
  ) {
    logPickedLayout("PlayerOnly");
    return (
      <PlayerOnly
        event={event}
        withThumbs={withThumbs}
        isOnlyVideo={isOnlyVideo}
        preview={preview}
        ispresenter={presenterMode}
        socket_wc={socketWc}
      />
    );
  }

  // Player + Slides
  if (
    checkLayout(active, audioSlidesLayout) ||
    checkLayout(active, videoSlidesLayout)
  ) {
    const layoutName = reverseLayout ? 'ReversedPlayerSlides' : 'PlayerSlides';
    logPickedLayout(layoutName);
    return reverseLayout ? (
      <ReversedPlayerSlides
        event={event}
        withThumbs={withThumbs}
        preview={preview}
        ispresenter={presenterMode}
        socket_wc={socketWc}
      />
    ) : (
      <PlayerSlides
        event={event}
        withThumbs={withThumbs}
        preview={preview}
        ispresenter={presenterMode}
        socket_wc={socketWc}
      />
    );
  }

  // Player + Chapters
  if (
    checkLayout(active, audioChaptersLayout) ||
    checkLayout(active, videoChaptersLayout)
  ) {
    logPickedLayout("PlayerChapters");
    return (
      <PlayerChapters
        event={event}
        withThumbs={withThumbs}
        preview={preview}
        isOnlyVideo={true}
        ispresenter={presenterMode}
        socket_wc={socketWc}
      />
    );
  }

  // Player + Slides + Chapters
  if (
    checkLayout(active, audioSlidesChaptersLayout) ||
    checkLayout(active, videoSlidesChaptersLayout)
  ) {
    const layoutName = reverseLayout ? 'ReversedPlayerSlidesChapters' : 'PlayerSlidesChapters';
    logPickedLayout(layoutName);
    return reverseLayout ? (
      <ReversedPlayerSlidesChapters
        event={event}
        withThumbs={withThumbs}
        preview={preview}
        ispresenter={presenterMode}
        socket_wc={socketWc}
      />
    ) : (
      <PlayerSlidesChapters
        event={event}
        withThumbs={withThumbs}
        preview={preview}
        ispresenter={presenterMode}
        socket_wc={socketWc}
      />
    );
  }

  // Fallback to Slides+Chapters layout
  logPickedLayout("PlayerSlidesChapters (Fallback)");
  return (
    <PlayerSlidesChapters
      event={event}
      withThumbs={withThumbs}
      preview={preview}
      ispresenter={presenterMode}
      socket_wc={socketWc}
    />
  );
}

const WebcastingPlatform = (props) => {
  const webcastingContext = useContext(WebcastingContext);
  const {
    loading,
    localData,
    authorization,
    updateLoginTime,
    branding,
    setUserStartTiming,
    getGeoData,
    addDeviceName,
    getGeoDataAndDeviceInfo,
    noRegID,
    onDemandFullScreenMode,
    referrerDomain,
  } = webcastingContext;

  const event = props?.event; // Already a flat event object now
  const eventIdFromRoute = props?.match?.match?.params?.eventId;
  const isTemplateClean = Boolean(
    props?.match?.location?.search?.includes("template=clean")
  );
  const controlbarDisabled = Boolean(
    props?.match?.location?.search?.includes("controlbar=no")
  );

  const [socketWc, setSocketWc] = useState(null);
  const geoPostedForRegIdRef = useRef(null);
  const geoPostedForNoRegIdRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [countdownDone, setCountdownDone] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const handleToggleTranscript = useCallback(
    () => setTranscriptOpen((prev) => !prev),
    []
  );
  const handleCloseTranscript = useCallback(() => setTranscriptOpen(false), []);

  const transcriptContextValue = useMemo(
    () => ({ isOpen: transcriptOpen, toggleTranscript: handleToggleTranscript }),
    [transcriptOpen, handleToggleTranscript]
  );
  const isPreview = Boolean(props.preview) || Boolean(
    props?.match?.location?.search?.includes("previewKey=")
  );

  // Derived UI values (logo, header, and layout info)
  const derived = useMemo(() => {
    if (!event) {
      return {
        logoPath: null,
        logoPadding: "10px",
        headerTitle: "",
        headerBackground: "#ffffff",
        withThumbs: false,
        presenterMode: "off",
        activeRegionsApi: [],
      };
    }

    const { activeRegionsApi, withThumbs, presenterMode } = computeActiveRegions(event);

    return {
      logoPath: getWebsiteLogoPath(event),
      logoPadding: getLogoPadding(event),
      headerTitle: getHeaderTitle(event),
      headerBackground: getHeaderBackground(event),
      withThumbs,
      presenterMode,
      activeRegionsApi,
    };
  }, [event]);

  // Socket connection
  useEffect(() => {
    //Namespace is statsSocket
    const socket = io(`${process.env.REACT_APP_SOCKET_IO_URL}/statsSocket`, {
      reconnectionDelay: 5000,
    });

    socket.on("connect", () => {
      if (socket.recovered) {
        const uid = getUserId({
          registrationRequired: event?.registrationRequired,
          authorization,
          noRegID,
        });
        console.log('uidFromWebcastingPlatform', uid)

        submitLogs(
          JSON.stringify({
            status: "RECOVERD",
            message: `Socket.io RECOVERD, for user ${uid}`,
          })
        );
      }
    });

    socket.on("reconnect", (attemptNumber) => {
      // eslint-disable-next-line no-console
      console.log(`Reconnected after setDelay ${attemptNumber} attempts`);
    });

    setSocketWc(socket);

    return () => {
      const uid = getUserId({
        registrationRequired: event?.registrationRequired,
        authorization,
        noRegID,
      });

      if (event?._id && uid) {
        socket.emit("leaveEvent", event._id, uid);
      }

      socket.off("connect");
      socket.off("reconnect");
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remove user after timeout for on-demand
  const removeFromLiveUsersList = useCallback(() => {
    setShowModal(true);

    const uid = getUserId({
      registrationRequired: event?.registrationRequired,
      authorization,
      noRegID,
    });

    if (socketWc && event?._id && uid) {
      socketWc.emit("leaveEvent", event._id, uid);
    }
  }, [socketWc, event?._id, event?.registrationRequired, authorization, noRegID]);

  useConditionalTimeout(removeFromLiveUsersList, 3600000, !event?.isLive && !isPreview);
  // useConditionalTimeout(removeFromLiveUsersList, 10000, !event?.isLive);

  // Geo/device tracking for reg users
  const postGeoAndDeviceForRegUser = useCallback(async () => {

    if (!event?.registrationRequired) return;

    const registrant = authorization;
    if (!registrant?._id) return;

    await getGeoData(eventIdFromRoute, registrant._id);
    await addDeviceName(registrant._id, eventIdFromRoute, window.navigator.platform);
  }, [
    event?.registrationRequired,
    authorization,
    getGeoData,
    addDeviceName,
    eventIdFromRoute,
  ]);

  // Geo/device/referrer for no-reg
  const postGeoDeviceAndReferrerForNoReg = useCallback(async () => {
    if (!noRegID || !event?._id) return;
    getGeoDataAndDeviceInfo(noRegID, event._id, document.referrer);
  }, [noRegID, event?._id, getGeoDataAndDeviceInfo]);

  // Run once per noRegID to avoid re-renders from setUserStartTiming / unstable callback refs
  useEffect(() => {
    if (!noRegID || !event?._id) return;
    if (geoPostedForNoRegIdRef.current === noRegID) return;
    geoPostedForNoRegIdRef.current = noRegID;
    postGeoDeviceAndReferrerForNoReg();
    setUserStartTiming(new Date().toUTCString());
  }, [noRegID, event?._id, postGeoDeviceAndReferrerForNoReg, setUserStartTiming]);

  // Reg users: geo/device + login time (once per registrant per mount to avoid loop)
  useEffect(() => {
    const registrant = authorization;
    const registrantId = registrant?._id;
    if (!registrantId) return;
    if (geoPostedForRegIdRef.current === registrantId) return;
    geoPostedForRegIdRef.current = registrantId;
    postGeoAndDeviceForRegUser();
    updateLoginTime(registrantId);
  }, [authorization, postGeoAndDeviceForRegUser, updateLoginTime]);

  // Reg users: GA + referrer domain
  useEffect(() => {
    if (!event?.registrationRequired) return;

    const registrant = authorization?.registrants?.[0];
    if (!registrant?._id) return;

    referrerDomain(registrant._id, document.referrer);

    ReactGA.initialize(`${process.env.REACT_APP_GOOGLE_ANALYTICS_ID}`, {
      debug: false,
      titleCase: false,
      redactEmail: false,
      gaOptions: { siteSpeedSampleRate: 100 },
    });
  }, [authorization, event?.registrationRequired, referrerDomain]);

  // On mobile + desktop Safari, try reconnect first when user returns.
  useEffect(() => {
    const mobileUaRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isTouchDevice = navigator.maxTouchPoints > 0;
    const userAgent = navigator.userAgent || "";
    const isMobileByUA = mobileUaRegex.test(userAgent);
    const isMobileLike = isMobileByUA || (isTouchDevice && window.innerWidth <= 1024);
    const isSafari = /^((?!chrome|android|crios|fxios|edg|opr).)*safari/i.test(userAgent);
    const isDesktopSafari = isSafari && !isMobileByUA;
    const shouldEnableAutoReload = isMobileLike || isDesktopSafari;

    if (!shouldEnableAutoReload) {
      console.log("[Visibility Test] reload disabled on this browser/device");
      return undefined;
    }

    let hasGoneBackground = false;
    let isReloading = false;

    const reloadOnReturn = (source) => {
      if (!hasGoneBackground || isReloading) return;
      hasGoneBackground = false;

      if (!socketWc) {
        isReloading = true;
        console.log(`[Visibility Test] user returned via ${source}, socket missing, reloading page`);
        window.location.reload();
        return;
      }

      if (socketWc.connected) {
        console.log(`[Visibility Test] user returned via ${source}, socket already connected`);
        return;
      }

      console.log(`[Visibility Test] user returned via ${source}, socket disconnected, reconnecting`);
      socketWc.connect();

      // If reconnect still fails after a short window, recover with full reload.
      window.setTimeout(() => {
        if (!socketWc.connected && !isReloading) {
          isReloading = true;
          console.log("[Visibility Test] reconnect failed after return, reloading page");
          window.location.reload();
        }
      }, 2500);
    };

    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === "visible";
      console.log(
        `[Visibility Test] tab is ${isVisible ? "VISIBLE" : "HIDDEN"} (state: ${document.visibilityState})`
      );
      if (!isVisible) {
        hasGoneBackground = true;
        return;
      }
      reloadOnReturn("visibilitychange");
    };

    const handlePageShow = () => {
      // Covers some mobile resume cases (device unlock / returning to browser).
      reloadOnReturn("pageshow");
    };

    const handleWindowBlur = () => {
      hasGoneBackground = true;
      console.log("[Visibility Test] window blur");
    };

    const handleWindowFocus = () => {
      console.log("[Visibility Test] window focus");
      reloadOnReturn("focus");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);

    // Initial log so you can confirm the listener is mounted.
    handleVisibilityChange();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [socketWc]);

  const markCountdownCompleted = useCallback(() => {
    setTimeout(() => setCountdownDone(true), 1000);
  }, []);

  const handleReload = useCallback(() => window.location.reload(), []);

  // Current layout selection based on new layout object
  const currentLayout = useMemo(() => {
    if (!event) return null;

    return pickLayoutComponent({
      event,
      socketWc,
      preview: props.preview,
      withThumbs: derived.withThumbs,
      presenterMode: derived.presenterMode,
      activeRegionsApi: derived.activeRegionsApi,
    });
  }, [
    event,
    socketWc,
    props.preview,
    derived.withThumbs,
    derived.presenterMode,
    derived.activeRegionsApi,
  ]);

  const showThumbs =
    Boolean(derived.withThumbs) &&
    !Boolean(event?.isLive) &&
    !Boolean(onDemandFullScreenMode);
  console.log('showThumbsFromWebcastingPlatform', showThumbs)
  const forcedLiveByAdmin = event?.webcast?.timing?.status === 'live';
  const showUpcoming =
    !forcedLiveByAdmin &&
    !countdownDone &&
    !isPreview &&
    event?.isLive === false &&
    // timeToEventMs(event) > 0;
    msUntilStart(event) > 0;
    console.log('showUpcomingFromWebcastingPlatform', showUpcoming)
  if (!event) return null;

  // controlPanel now primarily comes from layout.controlPanel, with fallback
  const layoutObj = event?.webcast?.layout || {};
  const controlPanelEnabled =
    typeof layoutObj.controlPanel === "boolean"
      ? layoutObj.controlPanel
      : Boolean(event?.webcast?.controlPanel);

  return (
    <SocketIOHOC
      socket_wc={socketWc}
      eventID={event._id}
      onDemand={!event.isLive}
      isEventLive={event.registrationRequired}
      authorization={authorization}
      noRegID={noRegID}
    >
      <>
        <LeaveUserModal
          open={showModal}
          onClose={() => setShowModal(false)}
          onReload={handleReload}
        />

        {/* {props.branding ? <HeadStyleForNoReg branding={props.branding} /> : <HeadStyle />} */}
                {(props.branding && event?._id) && <HeadStyle branding={props.branding} />}


        {showUpcoming ? (
          <UpcomingEvent
            match={props.match}
            loading={loading}
            event={event}
            saveAuth={true}
            authorization={props?.match?.match?.params?.confirmationId}
            localData={localData}
            timeToEvent={timeToEventMs(event)}
            brandingFound={branding}
            countDownCompleted={markCountdownCompleted}
          />
        ) : (
          <>
            {renderBackground({ event, isTemplateClean })}
            <CssBaseline />

            <TranscriptContext.Provider value={transcriptContextValue}>
            <div
              style={{
                width: (!isMobileOnly && transcriptOpen)
                  ? `calc(100vw - ${TRANSCRIPT_PANEL_W}px)`
                  : '100%',
                transition: 'width 0.3s ease',
                overflow: 'hidden',
              }}
            >
            <Container
              maxWidth="xl"
              className="mainContainer"
            >
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                id="firstRow"
                className="whiteBg"
              >
                {isTemplateClean ? (
                  <Box p={2} />
                ) : (
                  <HeaderBar
                    eventId={eventIdFromRoute}
                    eventHeaderTitle={derived.headerTitle}
                    logoPath={derived.logoPath}
                    logoPadding={derived.logoPadding}
                    headerBackground={derived.headerBackground}
                    brandingFound={branding}
                    isTransparent={event?.webcast?.branding?.colour?.transparentHeader}
                    titleStyles={
                      // Handle new structure: object with keys
                      event?.webcast?.branding?.fonts && typeof event.webcast.branding.fonts === 'object' && !Array.isArray(event.webcast.branding.fonts)
                        ? event.webcast.branding.fonts.title
                        : // Handle old structure: array
                          event?.webcast?.branding?.fonts?.[1]
                    }
                    event={event}
                  />
                )}
              </Grid>

              <Grid
                container
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                direction="row"
                spacing={0}
                id="secondRow"
                className="whiteBg"
              >
                {currentLayout}
              </Grid>

              <Suspense fallback={null}>
                {showThumbs && (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    id="thumbsBox"
                    className="thumbsContainer"
                  >
                    <Thumbs
                      slides={event?.slides}
                      preview={props.preview}
                      socket_wc={socketWc}
                    />
                  </Grid>
                )}
              </Suspense>
            </Container>
            </div>

            {controlbarDisabled || !controlPanelEnabled ? null : (
              socketWc && (
                <Toolbar
                  socket_wc={socketWc}
                  event={event}
                  preview={props.preview}
                  transcriptOpen={!isMobileOnly && transcriptOpen}
                  transcriptPanelWidth={TRANSCRIPT_PANEL_W}
                />
              )
            )}

            {event?.webcast?.layout?.showTranscript && (
              <TranscriptDrawer
                event={event}
                isOpen={transcriptOpen}
                onClose={handleCloseTranscript}
              />
            )}
            </TranscriptContext.Provider>
          </>
        )}
      </>
    </SocketIOHOC>
  );
};

export default React.memo(WebcastingPlatform);
